import { useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { useDebounce } from "@/hooks/useDebounce";
import { URL_SEARCH_DEBOUNCE_MS, useUrlSyncedFilters } from "@/hooks/useUrlSyncedFilters";
import {
  booleanCodec,
  dayRangeCodec,
  nullableBooleanCodec,
  nullableEnumCodec,
  stringCodec,
  readFiltersFromUrl,
  type UrlCodec,
  type UrlFilterCodecs,
} from "@/lib/urlFilters.util";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { COUNTRY_REGIONS, type RegionCode } from "@/constants/countries";

export interface ExpeditionFilters {
  search: string;
  searchExact: boolean;
  country: string;
  region: RegionCode | null;
  trackingCode: string;
  customId: string;
  userId: string;
  pickupZoneId: string;
  createdAt: DateRange | null;
  validatedAt: DateRange | null;
  pickupDate: DateRange | null;
  finalizedAt: DateRange | null;
  deliveryFromDate: DateRange | null;
  deletedAt: DateRange | null;
  deliveryDate: DateRange | null;
  draftOnly: boolean;
  draftBySelf: boolean;
  isRemote: boolean;
  finalizedById: string;
  finalizedBySelf: boolean;
  manualTracking: string;
  blockedManual: boolean | null;
  blockedReason: ExpeditionBlockedReasonEnum | null;
  expeditonType: ExpeditionTypeEnum | null;
  packagingType: PackagingFilterEnum | null;
  customQuotationType: CustomQuotationTypeEnum | null;
  sorting: {
    field: string;
    direction: "ASC" | "DESC";
  };
}

type FilterAction =
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_COUNTRY"; payload: string }
  | { type: "SET_REGION"; payload: RegionCode | null }
  | { type: "SET_TRACKING_CODE"; payload: string }
  | { type: "SET_CUSTOM_ID"; payload: string }
  | { type: "SET_USER_ID"; payload: string }
  | { type: "SET_PICKUP_ZONE_ID"; payload: string }
  | { type: "SET_DRAFT_ONLY"; payload: boolean }
  | { type: "SET_DRAFT_BY_SELF"; payload: boolean }
  | { type: "SET_IS_REMOTE"; payload: boolean }
  | { type: "SET_MANUAL_TRACKING"; payload: string }
  | { type: "SET_BLOCKED_MANUAL"; payload: boolean | null }
  | { type: "SET_BLOCKED_REASON"; payload: ExpeditionBlockedReasonEnum | null }
  | { type: "SET_FINALIZED_BY_ID"; payload: string }
  | { type: "SET_FINALIZED_BY_SELF"; payload: boolean }
  | { type: "SET_EXPEDITION_TYPE"; payload: ExpeditionTypeEnum | null }
  | { type: "SET_PACKAGING_TYPE"; payload: PackagingFilterEnum | null }
  | { type: "SET_CUSTOM_QUOTATION_TYPE"; payload: CustomQuotationTypeEnum | null }
  | { type: "SET_CREATED_AT_RANGE"; payload: DateRange | null }
  | { type: "SET_VALIDATED_AT_RANGE"; payload: DateRange | null }
  | { type: "SET_PICKUP_DATE_RANGE"; payload: DateRange | null }
  | { type: "SET_DELIVERY_FROM_DATE_RANGE"; payload: DateRange | null }
  | { type: "SET_FINALIZED_AT_RANGE"; payload: DateRange | null }
  | { type: "SET_DELETED_AT_RANGE"; payload: DateRange | null }
  | { type: "SET_DELIVERY_DATE_RANGE"; payload: DateRange | null }
  | { type: "SET_SORTING"; payload: { field: string; direction: "ASC" | "DESC" } }
  | { type: "RESET_FILTERS" }
  | { type: "RESET_ADVANCED_FILTERS" };

const initialState: ExpeditionFilters = {
  search: "",
  searchExact: false,
  country: "",
  region: null,
  trackingCode: "",
  customId: "",
  userId: "",
  manualTracking: "",
  pickupZoneId: "",
  pickupDate: null,
  validatedAt: null,
  deliveryFromDate: null,
  finalizedAt: null,
  deletedAt: null,
  deliveryDate: null,
  createdAt: null,
  draftOnly: false,
  draftBySelf: false,
  isRemote: false,
  expeditonType: null,
  customQuotationType: null,
  packagingType: null,
  blockedManual: null,
  blockedReason: null,
  finalizedById: "",
  finalizedBySelf: false,
  sorting: {
    field: "createdAt",
    direction: "DESC",
  },
};

function filterReducer(state: ExpeditionFilters, action: FilterAction): ExpeditionFilters {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: action.payload, searchExact: false };
    // `country` and `region` are exclusive: the query gives priority to the country,
    // so a region left behind would resurface as soon as the country is cleared.
    case "SET_COUNTRY":
      return { ...state, country: action.payload, region: action.payload ? null : state.region };
    case "SET_REGION":
      return { ...state, region: action.payload, country: "" };
    case "SET_TRACKING_CODE":
      return { ...state, trackingCode: action.payload };
    case "SET_CUSTOM_ID":
      return { ...state, customId: action.payload };
    case "SET_USER_ID":
      return { ...state, userId: action.payload };
    case "SET_PICKUP_ZONE_ID":
      return { ...state, pickupZoneId: action.payload };
    case "SET_DRAFT_ONLY":
      return { ...state, draftOnly: action.payload };
    case "SET_DRAFT_BY_SELF":
      return { ...state, draftBySelf: action.payload };
    case "SET_IS_REMOTE":
      return { ...state, isRemote: action.payload };
    case "SET_FINALIZED_BY_ID":
      return { ...state, finalizedById: action.payload };
    case "SET_FINALIZED_BY_SELF":
      return { ...state, finalizedBySelf: action.payload };
    case "SET_EXPEDITION_TYPE":
      return { ...state, expeditonType: action.payload };
    case "SET_MANUAL_TRACKING":
      return { ...state, manualTracking: action.payload };
    case "SET_BLOCKED_MANUAL":
      return { ...state, blockedManual: action.payload };
    case "SET_BLOCKED_REASON":
      return { ...state, blockedReason: action.payload };
    case "SET_CUSTOM_QUOTATION_TYPE":
      return { ...state, customQuotationType: action.payload };
    case "SET_PACKAGING_TYPE":
      return { ...state, packagingType: action.payload };
    case "SET_PICKUP_DATE_RANGE":
      return { ...state, pickupDate: action.payload };
    case "SET_VALIDATED_AT_RANGE":
      return { ...state, validatedAt: action.payload };
    case "SET_DELIVERY_FROM_DATE_RANGE":
      return { ...state, deliveryFromDate: action.payload };
    case "SET_FINALIZED_AT_RANGE":
      return { ...state, finalizedAt: action.payload };
    case "SET_DELETED_AT_RANGE":
      return { ...state, deletedAt: action.payload };
    case "SET_DELIVERY_DATE_RANGE":
      return { ...state, deliveryDate: action.payload };
    case "SET_CREATED_AT_RANGE":
      return { ...state, createdAt: action.payload };
    case "SET_SORTING":
      return { ...state, sorting: action.payload };
    case "RESET_FILTERS":
      return initialState;
    default:
      return state;
  }
}

const REGION_CODES = Object.keys(COUNTRY_REGIONS) as RegionCode[];
const PACKAGING_TYPES = Object.values(PackagingFilterEnum);
const EXPEDITION_TYPES = Object.values(ExpeditionTypeEnum);
const CUSTOM_QUOTATION_TYPES = Object.values(CustomQuotationTypeEnum);
const BLOCKED_REASONS = Object.values(ExpeditionBlockedReasonEnum);

const sortingCodec: UrlCodec<ExpeditionFilters["sorting"]> = {
  param: "sort",
  read: (raw) => {
    const [field, direction] = raw.split(":");
    if (!field || (direction !== "ASC" && direction !== "DESC")) return undefined;
    return { field, direction };
  },
  write: (value) =>
    value.field === initialState.sorting.field && value.direction === initialState.sorting.direction
      ? null
      : `${value.field}:${value.direction}`,
};

const FILTER_CODECS: UrlFilterCodecs<ExpeditionFilters> = {
  search: stringCodec("search"),
  searchExact: booleanCodec("exact"),
  country: stringCodec("country"),
  region: nullableEnumCodec("region", REGION_CODES),
  trackingCode: stringCodec("trackingCode"),
  customId: stringCodec("customId"),
  userId: stringCodec("userId"),
  pickupZoneId: stringCodec("pickupZone"),
  createdAt: dayRangeCodec("createdRange"),
  validatedAt: dayRangeCodec("validatedRange"),
  pickupDate: dayRangeCodec("pickupRange"),
  finalizedAt: dayRangeCodec("finalizedRange"),
  deliveryFromDate: dayRangeCodec("deliveryFromRange"),
  deletedAt: dayRangeCodec("deletedRange"),
  deliveryDate: dayRangeCodec("deliveryRange"),
  draftOnly: booleanCodec("draftOnly"),
  draftBySelf: booleanCodec("draftBySelf"),
  isRemote: booleanCodec("remote"),
  finalizedById: stringCodec("finalizedById"),
  finalizedBySelf: booleanCodec("finalizedBySelf"),
  manualTracking: stringCodec("tracking"),
  blockedManual: nullableBooleanCodec("blockedManual"),
  blockedReason: nullableEnumCodec("blockedReason", BLOCKED_REASONS),
  expeditonType: nullableEnumCodec("expeditionType", EXPEDITION_TYPES),
  packagingType: nullableEnumCodec("packaging", PACKAGING_TYPES),
  customQuotationType: nullableEnumCodec("customQuotationType", CUSTOM_QUOTATION_TYPES),
  sorting: sortingCodec,
};

function getInitialStateFromUrl(searchParams: URLSearchParams): ExpeditionFilters {
  const state = readFiltersFromUrl(searchParams, initialState, FILTER_CODECS);
  if (state.country) state.region = null;
  return state;
}

export const useExpeditionFilters = () => {
  const [searchParams] = useSearchParams();
  const [filters, dispatch] = useReducer(filterReducer, searchParams, getInitialStateFromUrl);
  const debouncedSearch = useDebounce(filters.search, URL_SEARCH_DEBOUNCE_MS);

  const syncedFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );
  useUrlSyncedFilters(syncedFilters, FILTER_CODECS);

  const actions = useMemo(
    () => ({
      setSearch: (search: string) => dispatch({ type: "SET_SEARCH", payload: search }),
      setCountry: (country: string | null) =>
        dispatch({ type: "SET_COUNTRY", payload: country ?? "" }),
      setRegion: (region: RegionCode | null) => dispatch({ type: "SET_REGION", payload: region }),
      setTrackingCode: (trackingCode: string) =>
        dispatch({ type: "SET_TRACKING_CODE", payload: trackingCode }),
      setCustomId: (customId: string | null) =>
        dispatch({ type: "SET_CUSTOM_ID", payload: customId ?? "" }),
      setUserId: (userId: string) => dispatch({ type: "SET_USER_ID", payload: userId }),
      setManualTracking: (manualTracking: string) =>
        dispatch({ type: "SET_MANUAL_TRACKING", payload: manualTracking }),
      setBlockedManual: (blockedManual: boolean | null) =>
        dispatch({ type: "SET_BLOCKED_MANUAL", payload: blockedManual }),
      setBlockedReason: (blockedReason: ExpeditionBlockedReasonEnum | null) =>
        dispatch({ type: "SET_BLOCKED_REASON", payload: blockedReason }),
      setPickupZoneId: (pickupZoneId: string) =>
        dispatch({ type: "SET_PICKUP_ZONE_ID", payload: pickupZoneId }),
      setDraftOnly: (draftOnly: boolean) =>
        dispatch({ type: "SET_DRAFT_ONLY", payload: draftOnly }),
      setDraftBySelf: (draftBySelf: boolean) =>
        dispatch({ type: "SET_DRAFT_BY_SELF", payload: draftBySelf }),
      setIsRemote: (isRemote: boolean) => dispatch({ type: "SET_IS_REMOTE", payload: isRemote }),
      setFinalizedById: (finalizedById: string) =>
        dispatch({ type: "SET_FINALIZED_BY_ID", payload: finalizedById }),
      setFinalizedBySelf: (finalizedBySelf: boolean) =>
        dispatch({ type: "SET_FINALIZED_BY_SELF", payload: finalizedBySelf }),
      setExpeditionType: (expeditionType: ExpeditionTypeEnum | null) =>
        dispatch({ type: "SET_EXPEDITION_TYPE", payload: expeditionType }),
      setCustomQuotationType: (customQuotationType: CustomQuotationTypeEnum | null) =>
        dispatch({ type: "SET_CUSTOM_QUOTATION_TYPE", payload: customQuotationType }),
      setPackagingType: (packagingType: PackagingFilterEnum | null) =>
        dispatch({ type: "SET_PACKAGING_TYPE", payload: packagingType }),
      setPickupDateRange: (dateRange: DateRange | null) =>
        dispatch({ type: "SET_PICKUP_DATE_RANGE", payload: dateRange }),
      setValidatedAtRange: (validatedAt: DateRange | null) =>
        dispatch({ type: "SET_VALIDATED_AT_RANGE", payload: validatedAt }),
      setDeliveryFromDateRange: (deliveryFromDate: DateRange | null) =>
        dispatch({ type: "SET_DELIVERY_FROM_DATE_RANGE", payload: deliveryFromDate }),
      setFinalizedAtRange: (finalizedAt: DateRange | null) =>
        dispatch({ type: "SET_FINALIZED_AT_RANGE", payload: finalizedAt }),
      setDeletedAtRange: (deletedAt: DateRange | null) =>
        dispatch({ type: "SET_DELETED_AT_RANGE", payload: deletedAt }),
      setDeliveryDateRange: (deliveryDate: DateRange | null) =>
        dispatch({ type: "SET_DELIVERY_DATE_RANGE", payload: deliveryDate }),
      setCreatedAtRange: (createdAt: DateRange | null) =>
        dispatch({ type: "SET_CREATED_AT_RANGE", payload: createdAt }),
      setSorting: (field: string, direction: "ASC" | "DESC") =>
        dispatch({ type: "SET_SORTING", payload: { field, direction } }),
      resetFilters: () => dispatch({ type: "RESET_FILTERS" }),
    }),
    [],
  );

  return useMemo(
    () => ({ filters, debouncedSearch, actions }),
    [filters, debouncedSearch, actions],
  );
};
